import { inject, Injectable, signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'

import { intersection } from 'lodash-es'
import { Nullable } from 'primeng/ts-helpers'
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  Observable,
  startWith,
  takeUntil,
  withLatestFrom,
} from 'rxjs'
import { map, shareReplay } from 'rxjs/operators'

import { AppFeatures, Driver } from '@ti/contracts'
import { ApiService } from '@ti/core/api/services/api.service'
import { FilterData, FilterValues } from '@ti/core/common/contracts'
import { Memoize } from '@ti/core/common/decorators/memoize'
import { NavigationHistoryProvider, PricingPlanService } from '@ti/core/common/services'
import { BrowserSessionStorage } from '@ti/core/common/storage'
import { completeStore, injectDestroy$ } from '@ti/core/common/utils'
import { UIVehicleAggregate } from '@ti/core/contracts/vehicle'
import { LanguageService } from '@ti/core/ui-kit/i18n'
import { DropdownSelectMenuItem } from '@ti/core/ui-kit/layout/contracts'
import { countFiltersValues } from '@ti/core/ui-kit/layout/utils'
import { DriversStore } from '@ti/shared/drivers/services'
import { GroupsOptionsProvider } from '@ti/shared/groups/services'
import { VehicleStatus } from '@ti/shared/map/contracts'

import { FleetStore } from './fleet.store'
import { VehiclesStore } from './vehicles.store'

export enum FleetListType {
  Vehicles = 'vehicles',
  Drivers = 'drivers',
}

export enum DriverSortOrder {
  NAME_ASC,
  NAME_DESC,
}

export enum VehicleSortOrder {
  NAME_ASC,
  NAME_DESC,
  MOVING_DESC,
  MOVING_ASC,
  IDLING_DESC,
  STOPPED_DESC,
}

@Injectable()
export class FleetViewModel {
  public isSearchOpened = signal(false)

  protected readonly api = inject(ApiService)
  protected readonly driverStore = inject(DriversStore)
  protected readonly fleetStore = inject(FleetStore)
  protected readonly groupsOptions = inject(GroupsOptionsProvider)
  protected readonly languageService = inject(LanguageService)
  protected readonly navigationHistoryProvider = inject(NavigationHistoryProvider)
  protected readonly pricingPlanService = inject(PricingPlanService)
  protected readonly sessionStorage = inject(BrowserSessionStorage)
  protected readonly vehiclesStore = inject(VehiclesStore)
  protected readonly destroy$ = injectDestroy$()

  protected readonly store = {
    driverOrder$: new BehaviorSubject<DriverSortOrder | undefined>(undefined),
    filterValue$: new BehaviorSubject<FilterValues | undefined>(undefined),
    isCompactMode$: new BehaviorSubject<boolean>(false),
    listType$: new BehaviorSubject<FleetListType>(FleetListType.Vehicles),
    search$: new BehaviorSubject(''),
    selectedDriverId$: new BehaviorSubject<string | undefined>(undefined),
    selectedVehicleId$: new BehaviorSubject<string | undefined>(undefined),
    vehicleOrder$: new BehaviorSubject<VehicleSortOrder | undefined>(undefined),
  }

  protected readonly DRIVERS_ORDER_STORAGE_KEY = 'FLEET_DRIVERS_ORDER'
  protected readonly FILTER_STORAGE_KEY = 'FLEET_FILTER'
  protected readonly LIST_TYPE_STORAGE_KEY = 'FLEET_LIST_TYPE'
  protected readonly SEARCH_STORAGE_KEY = 'FLEET_SEARCH'
  protected readonly VEHICLES_ORDER_STORAGE_KEY = 'FLEET_VEHICLES_ORDER'

  @Memoize()
  public get state() {
    const filtersData$ = this.createFilterOptionsStream()
    const driverSearchResults$ = this.createDriversSearchResultsStream()

    return {
      driverOrder$: this.store.driverOrder$.asObservable(),
      filterValue$: this.store.filterValue$.asObservable(),
      isCompactMode$: this.store.isCompactMode$.asObservable(),
      isLoading$: this.fleetStore.isLoading$,
      isVehicleListPending$: this.createVehicleListPendingStream(),
      listType$: this.store.listType$.asObservable(),
      search$: this.store.search$.asObservable(),
      selectedDriverId$: this.store.selectedDriverId$.asObservable(),
      selectedVehicleId$: this.store.selectedVehicleId$.asObservable(),
      vehicleOrder$: this.store.vehicleOrder$.asObservable(),
      driverOrderOptions$: this.createDriverOrderOptionsStream(),
      vehicleOrderOptions$: this.createVehicleOrderOptionsStream(),
      driverSearchResults$,
      driverVehicleSearchResults$:
        this.createDriverVehicleSearchResultsStream(driverSearchResults$),
      vehicleSearchResults$: this.createVehicleSearchResultsStream(),
      selectedDriver$: this.createSelectedDriverStream(),
      selectedVehicle$: this.createSelectedVehicleStream(),
      filtersData$: filtersData$,
      selectedFiltersCount$: this.createSelectedFiltersCountStream(filtersData$),
    }
  }

  // Signal-backed: templates read it while search/filter also change outside events
  private readonly searchOrFilterApplied = toSignal(
    combineLatest([this.store.search$, this.store.filterValue$]).pipe(
      map(([search, filterValue]) => !!(search || filterValue)),
    ),
    { requireSync: true },
  )

  public get isSearchOrFilterApplied() {
    return this.searchOrFilterApplied()
  }

  public hideSearch() {
    if (this.store.search$.value == '') {
      this.isSearchOpened.set(false)
      this.sessionStorage.removeItem(this.SEARCH_STORAGE_KEY)
    }
  }

  public clearSearch() {
    this.store.search$.next('')
    this.sessionStorage.removeItem(this.SEARCH_STORAGE_KEY)
    this.hideSearch()
  }

  public setSearch(text: string) {
    this.store.search$.next(text)
    this.sessionStorage.setItem(this.SEARCH_STORAGE_KEY, text)
  }

  public setDriverOrder(value: DriverSortOrder) {
    this.store.driverOrder$.next(value)
    this.sessionStorage.setItem(this.DRIVERS_ORDER_STORAGE_KEY, `${value}`)
  }

  public setVehicleOrder(value: VehicleSortOrder) {
    this.store.vehicleOrder$.next(value)
    this.sessionStorage.setItem(this.VEHICLES_ORDER_STORAGE_KEY, `${value}`)
  }

  public selectDriver(driverId: Nullable<string>) {
    this.store.selectedDriverId$.next(
      (this.store.selectedDriverId$.value === driverId ? null : driverId) ?? undefined,
    )
  }

  public selectVehicle(vehicleId: Nullable<string>) {
    this.store.selectedVehicleId$.next(
      (this.store.selectedVehicleId$.value === vehicleId ? null : vehicleId) ?? undefined,
    )
  }

  public setFilter(filters?: FilterValues) {
    this.store.filterValue$.next(filters)
    this.sessionStorage.setItem(this.FILTER_STORAGE_KEY, JSON.stringify(filters))
  }

  public setListType(listType: FleetListType) {
    const current = this.store.listType$.value
    const newListType = listType ?? (current === 'drivers' ? 'vehicles' : 'drivers')
    this.store.listType$.next(newListType)
    this.sessionStorage.setItem(this.LIST_TYPE_STORAGE_KEY, newListType)
  }

  public setIsCompactMode(isCompactMode?: boolean) {
    this.store.isCompactMode$.next(!!isCompactMode)
  }

  public init() {
    this.initSelectedEntitySync()

    const previousUrl = this.navigationHistoryProvider.previousUrl$.value
    if (!previousUrl || previousUrl.startsWith('fleet/vehicles')) {
      this.restoreOrder()
      this.restoreSearch()
      this.restoreFilters()
      this.restoreListType()
    } else {
      this.clearCache()
    }

    this.destroy$.subscribe(() => completeStore(this.store))
  }

  protected clearCache() {
    this.sessionStorage.removeItem(this.DRIVERS_ORDER_STORAGE_KEY)
    this.sessionStorage.removeItem(this.FILTER_STORAGE_KEY)
    this.sessionStorage.removeItem(this.LIST_TYPE_STORAGE_KEY)
    this.sessionStorage.removeItem(this.SEARCH_STORAGE_KEY)
    this.sessionStorage.removeItem(this.VEHICLES_ORDER_STORAGE_KEY)
  }

  protected initSelectedEntitySync() {
    this.state.selectedDriver$
      .pipe(
        takeUntil(this.destroy$),
        filter(() => this.store.listType$.getValue() === FleetListType.Drivers),
        distinctUntilChanged((prev, next) => prev?.id === next?.id),
        withLatestFrom(this.fleetStore.vehicles$, this.store.selectedVehicleId$),
      )
      .subscribe(([driver, vehicles, selectedVehicleId]) => {
        const relatedVehicleId = driver?.vehicleId
          ? vehicles.find((v) => v.id === driver.vehicleId)?.id
          : undefined
        if (relatedVehicleId !== selectedVehicleId) {
          this.selectVehicle(relatedVehicleId)
        }
      })

    this.state.selectedVehicle$
      .pipe(
        takeUntil(this.destroy$),
        filter(() => this.store.listType$.getValue() === FleetListType.Vehicles),
        distinctUntilChanged((prev, next) => prev?.id === next?.id),
        withLatestFrom(this.driverStore.state$.items, this.store.selectedDriverId$),
      )
      .subscribe(([vehicle, drivers, selectedDriverId]) => {
        const relatedDriverId = vehicle?.driverId
          ? drivers.find((d) => d.id === vehicle?.driverId)?.id
          : undefined
        if (relatedDriverId !== selectedDriverId) {
          this.selectDriver(relatedDriverId)
        }
      })
  }

  protected restoreOrder() {
    const driverOrder = this.sessionStorage.getItem(this.DRIVERS_ORDER_STORAGE_KEY)
    this.setVehicleOrder(driverOrder ? parseInt(driverOrder) : VehicleSortOrder.NAME_ASC)

    const vehicleOrder = this.sessionStorage.getItem(this.VEHICLES_ORDER_STORAGE_KEY)
    this.setVehicleOrder(vehicleOrder ? parseInt(vehicleOrder) : VehicleSortOrder.NAME_ASC)
  }

  protected restoreSearch() {
    const searchHistory = this.sessionStorage.getItem(this.SEARCH_STORAGE_KEY)
    if (searchHistory) {
      this.setSearch(searchHistory)
      this.isSearchOpened.set(true)
    }
  }

  protected restoreFilters() {
    const filtersHistory = this.sessionStorage.getItem(this.FILTER_STORAGE_KEY)
    if (filtersHistory) {
      this.setFilter(JSON.parse(filtersHistory))
    }
  }

  protected restoreListType() {
    const listTypeHistory = this.sessionStorage.getItem(this.LIST_TYPE_STORAGE_KEY)
    if (listTypeHistory) {
      this.setListType(listTypeHistory as FleetListType)
    }
  }

  protected createDriverOrderOptionsStream(): Observable<DropdownSelectMenuItem[]> {
    return this.languageService
      .massTranslate$({
        nameAsc: 'fleet.sort.name-asc',
        nameDesc: 'fleet.sort.name-desc',
      })
      .pipe(
        takeUntil(this.destroy$),
        map((labels) => {
          return [
            { label: labels.nameAsc, value: VehicleSortOrder.NAME_ASC },
            { label: labels.nameDesc, value: VehicleSortOrder.NAME_DESC },
          ]
        }),
      )
  }

  protected createVehicleOrderOptionsStream(): Observable<DropdownSelectMenuItem[]> {
    return this.languageService
      .massTranslate$({
        nameAsc: 'fleet.sort.name-asc',
        nameDesc: 'fleet.sort.name-desc',
        movingAsc: 'fleet.sort.moving-desc',
        movingDesc: 'fleet.sort.stopped-desc',
        idlingDesc: 'fleet.sort.idling-desc',
      })
      .pipe(
        takeUntil(this.destroy$),
        map((labels) => {
          return [
            { label: labels.nameAsc, value: VehicleSortOrder.NAME_ASC },
            { label: labels.nameDesc, value: VehicleSortOrder.NAME_DESC },
            { label: labels.movingAsc, value: VehicleSortOrder.MOVING_DESC },
            { label: labels.movingDesc, value: VehicleSortOrder.STOPPED_DESC },
            { label: labels.idlingDesc, value: VehicleSortOrder.IDLING_DESC },
          ]
        }),
      )
  }

  protected createDriversSearchResultsStream() {
    const filterResolverMap = {
      driverLinked: (driver: Driver, driverLinked: boolean[]) => {
        return driverLinked.length === 1 ? driverLinked[0] === !!driver.vehicleId : true
      },
      groupId: (driver: Driver, groupIds: string[]) => {
        return !!intersection(driver.groupIds, groupIds)?.length
      },
    } as Record<string, (driver: Driver, value: any) => boolean>

    const searchKeysFunctionsArray = [
      (driver: Driver) => driver.name,
      (driver: Driver) => driver.phoneNumber,
    ]

    return combineLatest([
      this.driverStore.state$.items,
      this.store.driverOrder$,
      this.store.search$,
      this.store.filterValue$,
    ]).pipe(
      takeUntil(this.destroy$),
      map(([all, order, search, filters]) => {
        return all
          .filter((driver) => {
            if (!driver.isActive) return false

            if (filters) {
              // Apply filters
              for (const key of Object.keys(filters)) {
                if (filterResolverMap[key] && filters[key]?.length) {
                  const matched = filterResolverMap[key](driver, filters[key])
                  if (!matched) return false
                }
              }
            }
            return true
          })
          .filter((driver) => {
            for (const cb of searchKeysFunctionsArray) {
              const value = `${cb(driver)}`
              if (value.toLowerCase().includes(search.toLowerCase())) {
                return true
              }
            }
          })
          .sort((a, b) => {
            if (order === DriverSortOrder.NAME_ASC) {
              return a.name?.localeCompare(b.name)
            }
            if (order === DriverSortOrder.NAME_DESC) {
              return -a.name?.localeCompare(b.name)
            }
            return 0
          })
      }),
    )
  }

  // Cached vehicles end isLoading before the aggregated vehicles first emit, and a reload keeps the
  // shown vehicles instead of the skeleton
  protected createVehicleListPendingStream(): Observable<boolean> {
    return combineLatest([
      this.fleetStore.isLoading$,
      this.fleetStore.vehicles$.pipe(
        map((vehicles) => vehicles.length),
        startWith(null),
      ),
    ]).pipe(
      takeUntil(this.destroy$),
      map(([isLoading, count]) => count === null || (isLoading && count === 0)),
      distinctUntilChanged(),
    )
  }

  protected createDriverVehicleSearchResultsStream(
    driverSearchResults$: Observable<Driver[]>,
  ): Observable<UIVehicleAggregate[]> {
    return combineLatest([driverSearchResults$, this.fleetStore.vehicles$]).pipe(
      takeUntil(this.destroy$),
      map(([drivers, vehicles]) => {
        const driverIds = new Set(drivers.map((driver) => driver.id))
        return vehicles.filter(
          (vehicle) => !!vehicle.driverData && driverIds.has(vehicle.driverData.id),
        )
      }),
    )
  }

  protected createVehicleSearchResultsStream(): Observable<UIVehicleAggregate[]> {
    const filterResolverMap = {
      driverLinked: (vehicle: UIVehicleAggregate, driverLinked: boolean[]) => {
        return driverLinked.length === 1 ? driverLinked[0] === !!vehicle.driverId : true
      },
      vehicleStatus: (vehicle: UIVehicleAggregate, vehicleStatuses: VehicleStatus[]) => {
        return vehicleStatuses.includes(vehicle.telemetry?.lastStatus as VehicleStatus)
      },
      groupId: (vehicle: UIVehicleAggregate, groupIds: string[]) => {
        return !!intersection(vehicle.groupIds, groupIds)?.length
      },
      vehicleIds: (vehicle: UIVehicleAggregate, vehicleIds: string[]) => {
        return !!intersection(vehicleIds, [vehicle.id])?.length
      },
    } as Record<string, (vehicle: UIVehicleAggregate, value: any) => boolean>

    const searchKeysFunctionsArray = [
      (v: UIVehicleAggregate) => v.make,
      (v: UIVehicleAggregate) => v.model,
      (v: UIVehicleAggregate) => v.name,
      (v: UIVehicleAggregate) => v.year,
      (v: UIVehicleAggregate) => v.licensePlate,
      (v: UIVehicleAggregate) => v.primaryExternalDeviceId,
      (v: UIVehicleAggregate) => v.primaryDeviceId,
      (v: UIVehicleAggregate) => v.primaryDevice?.identifier,
      (v: UIVehicleAggregate) => v.telemetry.address,
      (v: UIVehicleAggregate) => v.telemetry.geofenceNames?.join(', '),
    ]

    const compareByStatus = (
      a: UIVehicleAggregate,
      b: UIVehicleAggregate,
      status: VehicleStatus,
    ) => {
      if (
        (a.telemetry.lastStatus === status && b.telemetry.lastStatus !== status) ||
        (a.telemetry.lastStatus !== status && b.telemetry.lastStatus === status)
      ) {
        return a.telemetry.lastStatus === status ? -1 : 1
      }
      return 0
    }

    return combineLatest([
      this.fleetStore.vehicles$,
      this.store.vehicleOrder$,
      this.store.search$,
      this.store.filterValue$,
    ]).pipe(
      takeUntil(this.destroy$),
      map(([all, order, search, filters]) => {
        return all
          .filter((vehicle) => {
            if (filters) {
              // Apply filters
              for (const key of Object.keys(filters)) {
                if (filterResolverMap[key] && filters[key]?.length) {
                  const matched = filterResolverMap[key](vehicle, filters[key])
                  if (!matched) return false
                }
              }
            }
            return true
          })
          .sort((a, b) => {
            if (order === VehicleSortOrder.NAME_ASC) {
              return a.name?.localeCompare(b.name)
            }

            if (order === VehicleSortOrder.NAME_DESC) {
              return -a.name?.localeCompare(b.name)
            }

            if (order === VehicleSortOrder.MOVING_ASC || order === VehicleSortOrder.MOVING_DESC) {
              const byStatusRes = compareByStatus(a, b, VehicleStatus.Running)
              if (byStatusRes !== 0) {
                return byStatusRes
              }
              if (
                a.telemetry.lastStatus === VehicleStatus.Running &&
                b.telemetry.lastStatus === VehicleStatus.Running
              ) {
                if (a.telemetry.lastStatusInvalid || b.telemetry.lastStatusInvalid) {
                  return (
                    Number(a.telemetry.lastStatusInvalid) - Number(b.telemetry.lastStatusInvalid)
                  )
                }
                return a.name?.localeCompare(b.name)
              }
              return order === VehicleSortOrder.MOVING_ASC
                ? (a.telemetry.lastStatusChangeAt ?? 0) - (b.telemetry.lastStatusChangeAt ?? 0)
                : (b.telemetry.lastStatusChangeAt ?? 0) - (a.telemetry.lastStatusChangeAt ?? 0)
            }

            if (order === VehicleSortOrder.STOPPED_DESC) {
              if (
                (a.telemetry.lastStatus === VehicleStatus.Idling ||
                  a.telemetry.lastStatus === VehicleStatus.Stopped) &&
                (b.telemetry.lastStatus === VehicleStatus.Idling ||
                  b.telemetry.lastStatus === VehicleStatus.Stopped)
              ) {
                return (a.telemetry.lastStatusChangeAt ?? 0) - (b.telemetry.lastStatusChangeAt ?? 0)
              }
              const byStatusRes = compareByStatus(a, b, VehicleStatus.Running)
              if (byStatusRes !== 0) {
                return -byStatusRes
              }
              return a.name?.localeCompare(b.name)
            }

            if (order === VehicleSortOrder.IDLING_DESC) {
              const byStatusRes = compareByStatus(a, b, VehicleStatus.Idling)
              if (byStatusRes !== 0) {
                return byStatusRes
              }
              if (a.telemetry.lastStatusInvalid || b.telemetry.lastStatusInvalid) {
                return Number(a.telemetry.lastStatusInvalid) - Number(b.telemetry.lastStatusInvalid)
              }
              if (a.telemetry.lastStatus === VehicleStatus.Idling) {
                return (a.telemetry.lastStatusChangeAt ?? 0) - (b.telemetry.lastStatusChangeAt ?? 0)
              }
              return a.name?.localeCompare(b.name)
            }

            return 0
          })
          .filter((vehicle: UIVehicleAggregate) => {
            for (const cb of searchKeysFunctionsArray) {
              const value = `${cb(vehicle)}`
              if (value.toLowerCase().includes(search.toLowerCase())) {
                return true
              }
            }
          })
      }),
    )
  }

  protected createSelectedDriverStream() {
    return combineLatest([this.driverStore.state$.items, this.store.selectedDriverId$]).pipe(
      takeUntil(this.destroy$),
      map(([drivers, selectedId]) => {
        return selectedId ? drivers.find((item) => item.id === selectedId) : undefined
      }),
      distinctUntilChanged((prev, next) => prev?.id === next?.id),
    )
  }

  protected createSelectedVehicleStream() {
    return combineLatest([this.fleetStore.vehicles$, this.store.selectedVehicleId$]).pipe(
      takeUntil(this.destroy$),
      map(([vehicles, selectedId]) => {
        return selectedId ? vehicles.find((item) => item.id === selectedId) : undefined
      }),
      distinctUntilChanged((prev, next) => prev?.id === next?.id),
    )
  }

  protected createFilterOptionsStream() {
    return combineLatest([
      this.store.listType$,
      this.vehiclesStore.state$.items,
      this.groupsOptions.filterOptions$,
      this.pricingPlanService.areFeaturesAllowed([AppFeatures.FleetDrivers]),
      this.pricingPlanService.areFeaturesAllowed$([AppFeatures.GroupsAndLabels]),
      this.languageService.massTranslate$({
        driverStatus: 'fleet.filters.driver-status',
        vehicleStatus: 'fleet.filters.vehicle-status',
        driverLinked: 'fleet.filters.driver-linked',
        noDriverLinked: 'fleet.filters.no-driver-linked',
        vehicles: 'fleet.filters.vehicles',
        groups: 'fleet.filters.groups',
        moving: 'fleet.badge.moving',
        stopped: 'fleet.badge.stopped',
        idling: 'fleet.badge.idling',
      }),
    ]).pipe(
      map(
        ([fleetListType, vehicles, groupFilterOptions, driversEnabled, groupsEnabled, labels]) => {
          const options: FilterData[] = []

          if (driversEnabled) {
            options.push({
              key: 'driverLinked',
              type: 'list-multiselect',
              title: labels.driverStatus,
              options: [
                {
                  label: labels.driverLinked,
                  value: true,
                },
                {
                  label: labels.noDriverLinked,
                  value: false,
                },
              ],
              searchable: false,
            })
          }

          if (fleetListType === FleetListType.Vehicles) {
            options.push({
              key: 'vehicleStatus',
              type: 'list-multiselect',
              title: labels.vehicleStatus,
              options: [
                {
                  label: labels.moving,
                  value: VehicleStatus.Running,
                },
                {
                  label: labels.idling,
                  value: VehicleStatus.Idling,
                },
                {
                  label: labels.stopped,
                  value: VehicleStatus.Stopped,
                },
              ],
              searchable: false,
            })
          }

          if (groupsEnabled) {
            options.push({
              key: 'groupId',
              type: 'list-multiselect',
              title: labels.groups,
              options: groupFilterOptions,
              searchable: true,
            })
          }

          if (fleetListType === FleetListType.Vehicles) {
            options.push({
              key: 'vehicleIds',
              type: 'list-multiselect',
              title: labels.vehicles,
              options: vehicles.map((vehicle) => ({
                label: vehicle.name,
                value: vehicle.id,
              })),
              searchable: true,
            })
          }

          return options
        },
      ),
      takeUntil(this.destroy$),
      shareReplay({ bufferSize: 1, refCount: false }),
    )
  }

  protected createSelectedFiltersCountStream(filtersData$: Observable<FilterData[]>) {
    return combineLatest([this.store.filterValue$, filtersData$]).pipe(
      takeUntil(this.destroy$),
      map(([filterValue, filterData]) => countFiltersValues(filterData, filterValue)),
    )
  }
}
